import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showSpinner, closeSpinner, modal, confirm } from "../../../js/modules/Dialogs";
import { setUser } from "../../../services/adminService";
import ReactSelect from "react-select";

import { sessionExpired, adminSaveChangesConfirm, adminClientCannotPreactivate, adminClientCannotReset } from "../../../config/messages";
import { toScreen } from "../../../js/modules/Routing";
import { BellIcon, BellSlashIcon, CloseIcon, DiscountIcon, EmailIcon } from "../../commonUI/Icons/SvgIcon";
import { toast } from "react-toastify";

const ClientEdit = ({ view, client, onClose, userDetails }) => {
  const isEdit = view === "editprofile";
  const navigate = useNavigate();

  // Profile form state
  const [formData, setFormData] = useState({
    firstName: client?.accountInfo?.firstname || "",
    lastName: client?.accountInfo?.surname || "",
    username: client?.accountInfo?.username || "",
    email: client?.accountInfo?.email || "",
    phone: client?.accountInfo?.telephone || "",
    status: client?.status || "",
  });

  // Notification settings state
  const [settings, setSettings] = useState({
    emailnotifications:
      client?.accountInfo?.settings?.emailnotifications || false,
    holidaymode: client?.accountInfo?.settings?.holidaymode || false,
    marketingnotifications:
      client?.accountInfo?.settings?.marketingnotifications || false,
    pushnotifications:
      client?.accountInfo?.settings?.pushnotifications || false,
  });

  // API response handler
  const handleSaveResponse = useCallback(
    (resStatus, responseString, resJson) => {
      closeSpinner();

      switch (resStatus) {
        case 401:
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
          break;
        case 400:
          modal(responseString);
          break;
        case 200:
          toast.success("Changes successfully saved");
          toScreen(false, navigate, "/clients");
          break;
        default:
          modal("An error occurred. Please try again.");
      }
    },
    [navigate]
  );

  // Save client data
  const handleSave = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      showSpinner();

      const userData = {
        accountInfo: {
          username: userDetails?.accountInfo?.username,
          email: userDetails?.accountInfo?.email,
        },
        user: {
          _id: client._id,
          short_id: client.short_id,
          notes: client?.notes,
          accountInfo: {
            username: formData.username,
            firstname: formData.firstName,
            surname: formData.lastName,
            telephone: formData.phone,
            email: formData.email,
            settings: settings,
            avatar: client?.accountInfo?.avatar || "",
          },
          userType: client.userType,
          status: formData.status,
        },
      };
      setUser(userData, handleSaveResponse, userDetails?.jwttoken);
    },
    [userDetails, client, handleSaveResponse, settings, formData]
  );
  const handleInputChange = useCallback(
    (eventOrValue, actionMeta) => {
      const isReactSelect = !!actionMeta;

      let name, value;

      if (isReactSelect) {
        name = actionMeta?.name;
        value = eventOrValue?.value ?? "";
      } else {
        const {
          name: inputName,
          value: inputValue,
          type,
          checked,
        } = eventOrValue.target;
        name = inputName;
        value = type === "checkbox" ? checked : inputValue;
      }
      // Update notification settings
      if (
        [
          "pushnotifications",
          "emailnotifications",
          "marketingnotifications",
          "holidaymode",
        ].includes(name)
      ) {
        setSettings((prev) => ({
          ...prev,
          [name]: value,
        }));
        return;
      }

      // Update profile fields (formData)
      setFormData((prev) => {
        const updated = {
          ...prev,
          [name]: value,
        };

        // Handle special logic for status change
        if (name === "status") {
          if (value === "active" && client?.status === "delete") {
            console.log("Reactivating deleted client:", client?.id);
          }

          if (value === "preactivation" && client?.status !== "preactivation") {
            modal(adminClientCannotPreactivate);
          } else if (value === "resetting" && client?.status !== "resetting") {
            modal(adminClientCannotReset);
          }
        }

        return updated;
      });
    },
    [client]
  );

  // Form field configuration
  const profileFields = [
    { name: "status", label: "Status", value: formData.status },
    { name: "firstName", label: "First Name", value: formData.firstName },
    { name: "lastName", label: "Last Name", value: formData.lastName },
    { name: "username", label: "User Name", value: formData.username },
    { name: "email", label: "Email", value: formData.email, type: "email" },
    { name: "phone", label: "Telephone", value: formData.phone, type: "tel" },
  ];

  // Notification configuration
  const notificationSettings = [
    {
      name: "pushnotifications",
      title: "Push notifications",
      icon: <BellIcon />,
      description: "User receives job alerts and updates on their devices",
      checked: settings?.pushnotifications,
    },
    {
      name: "emailnotifications",
      title: "Email notifications",
      icon: <EmailIcon />,
      description: "User receives job alerts and updates via email",
      checked: settings?.emailnotifications,
    },
    {
      name: "marketingnotifications",
      title: "Marketing notifications",
      icon: <DiscountIcon />,
      description: "User receives promotional and marketing updates",
      checked: settings?.marketingnotifications,
    },
    {
      name: "holidaymode",
      title: "Holiday mode",
      icon: <BellSlashIcon />,
      description: "Pause all notifications while user is on holiday",
      checked: settings?.holidaymode,
    },
  ];

  const option = [
    { value: "preactivation", label: "Pending" },
    { value: "active", label: "Active" },
    { value: "blocked", label: "Blocked" },
    { value: "delete", label: "Delete" },
  ];

  return (
    <div className="right-modal discount-modal">
      {/* Modal Header */}
      <div className="modal-header">
        <div className="modal-title">
          <h2>
            <strong>{isEdit ? "Edit Profile" : "Edit Notifications"}</strong>
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <CloseIcon/>
          </button>
        </div>
      </div>

      {/* Modal Body */}
      <div className="modal-body">
        {isEdit ? (
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label htmlFor={name} className="form-label" key={name}>
                Status
              </label>

              <ReactSelect
                isClearable
                options={option}
                classNamePrefix="custom-select"
                name="status"
                placeholder="Select Status"
                value={option?.find((opt) => opt.value === formData?.status)}
                onChange={handleInputChange}
              />
            </div>

            {profileFields?.map(
              ({ name, label, value, type = "text" }, idx) => {
                if (idx === 0) return null;

                return (
                  <div className="form-group" key={name}>
                    <label htmlFor={name} className="form-label">
                      {label}
                    </label>
                    <input
                      type={type}
                      id={name}
                      name={name}
                      className="form-control"
                      value={value}
                      onChange={handleInputChange}
                      required={["email", "firstName", "lastName"].includes(
                        name
                      )}
                    />
                  </div>
                );
              }
            )}
          </form>
        ) : (
          // Notification Settings
          <ul className="notification-list">
            {notificationSettings?.map(
              ({ icon, name, title, description, checked }) => (
                <li
                  className="notification-list-item notification-modal-card"
                  key={name}
                >
                  <div>
                    <div className="d-flex align-items-center g-8">
                      {icon}
                      <span className="text-sm">{title}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{description}</p>
                  </div>
                  <label htmlFor={name} className="switch">
                    <input
                      id={name}
                      name={name}
                      type="checkbox"
                      onChange={handleInputChange}
                      checked={checked}
                    />
                    <span className="slider"></span>
                  </label>
                </li>
              )
            )}
          </ul>
        )}
      </div>

      {/* Modal Footer */}
      <div className="modal-footer">
        <div className="d-flex align-items-center g-12">
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleSave}
          >
            Save Changes
          </button>
          <button className="btn btn-outline-primary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientEdit;
